import { useEffect, useRef } from 'react';
import { useMapViewContext } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import Extent from '@arcgis/core/geometry/Extent';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import { useMapPreview } from './MapPreviewContext';

// Aksent-blå fra Origo (--accent-default), brukt for fremheving i kart.
const ACCENT: [number, number, number] = [0, 55, 248];

const pointSymbol = new SimpleMarkerSymbol({
    style: 'circle',
    color: [...ACCENT, 0.85],
    size: 8,
    outline: { color: [255, 255, 255, 0.9], width: 1 },
});
const lineSymbol = new SimpleLineSymbol({ color: [...ACCENT, 0.9], width: 2 });
const fillSymbol = new SimpleFillSymbol({
    color: [...ACCENT, 0.15],
    outline: { color: [...ACCENT, 0.9], width: 1.5 },
});

interface EsriGeometryJson {
    x?: number;
    y?: number;
    rings?: number[][][];
    paths?: number[][][];
    points?: number[][];
    spatialReference?: { wkid?: number };
}

/** Bygg en ArcGIS-geometri fra ESRI-JSON ved å se på formen. */
function buildGeometry(raw: unknown, wkid?: number | null) {
    if (!raw || typeof raw !== 'object') return null;
    const g = raw as EsriGeometryJson;
    const sr = g.spatialReference ?? (wkid ? { wkid } : undefined);

    if (Array.isArray(g.rings)) return Polygon.fromJSON({ rings: g.rings, spatialReference: sr });
    if (Array.isArray(g.paths)) return Polyline.fromJSON({ paths: g.paths, spatialReference: sr });
    if (Array.isArray(g.points)) return Multipoint.fromJSON({ points: g.points, spatialReference: sr });
    if (typeof g.x === 'number' && typeof g.y === 'number') {
        return new Point({ x: g.x, y: g.y, spatialReference: sr as { wkid: number } });
    }
    return null;
}

function symbolFor(geometryType: string): SimpleMarkerSymbol | SimpleLineSymbol | SimpleFillSymbol {
    switch (geometryType) {
        case 'polygon':
            return fillSymbol;
        case 'polyline':
            return lineSymbol;
        default:
            return pointSymbol;
    }
}

/**
 * Tegner forhåndsvisnings-features (fra MapPreviewContext) på et dedikert
 * GraphicsLayer og zoomer til dem. Tømmer laget når forhåndsvisningen fjernes.
 */
export function MapPreviewLayer() {
    const { mapView } = useMapViewContext();
    const { preview } = useMapPreview();
    const layerRef = useRef<GraphicsLayer | null>(null);

    // Opprett/fjern det dedikerte laget sammen med kartet.
    useEffect(() => {
        if (!mapView?.map) return;
        const layer = new GraphicsLayer({ title: 'Synkadmin – forhåndsvisning', listMode: 'hide' });
        mapView.map.add(layer);
        layerRef.current = layer;
        return () => {
            mapView.map?.remove(layer);
            layerRef.current = null;
        };
    }, [mapView]);

    useEffect(() => {
        const layer = layerRef.current;
        if (!layer || !mapView) return;

        layer.removeAll();

        if (!preview) return;

        const wkid = preview.wkid ?? preview.result.metadata.spatialReferenceWkid ?? undefined;
        const graphics: Graphic[] = [];

        for (const feature of preview.result.features) {
            const geometry = buildGeometry(feature.geometry, wkid);
            if (!geometry) continue;
            graphics.push(
                new Graphic({
                    geometry,
                    symbol: symbolFor(geometry.type),
                    attributes: feature.attributes,
                }),
            );
        }

        layer.addMany(graphics);

        // Zoom: til geometriene hvis vi har noen, ellers til oppgitt extent.
        if (graphics.length > 0) {
            mapView.goTo(graphics).catch(() => { /* avbrutt av ny navigasjon */ });
        } else if (preview.extent && wkid) {
            const extent = new Extent({
                xmin: preview.extent.xmin,
                ymin: preview.extent.ymin,
                xmax: preview.extent.xmax,
                ymax: preview.extent.ymax,
                spatialReference: { wkid },
            });
            mapView.goTo(extent).catch(() => { /* avbrutt */ });
        }
    }, [preview, mapView]);

    return null;
}
